import { ChangeDetectionStrategy, Component, OnInit, signal, computed, inject } from '@angular/core';
import { AuthService, BlockAbstractComponent, ParserService } from '@gallaghercp/core-frontend';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

interface SmpiContent {
    SMPI_TABLE: number;
    SMPI_TRA: string;
    SMPI_ENROLLED: string;
    TOTAL_CFV: string;
    GLIDEPATH_SMPI: string;
    GLIDEPATH?: number;
    SCENARIO_A_FUND: string;
    SCENARIO_A_PENSION: string;
    SCENARIO_B_FUND: string;
    SCENARIO_B_PENSION: string;
    [key: string]: any;
}

@Component({
    selector: 'core-block-user-smpi-table',
    templateUrl: './block-user-smpi-table.component.html',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockUserSmpiTableComponent extends BlockAbstractComponent implements OnInit {
    protected override parser: ParserService;
    protected override auth: AuthService;
    protected http = inject(HttpClient);

    loading = signal(true);
    smpiYears = signal<string[]>([]);
    smpiByYear = signal<Record<string, SmpiContent>>({});
    activeYear = signal<string>('');

    // CMS-configurable labels with sensible defaults
    get fundColumnHeading(): string {
        return this.block?.data?.fundColumnHeading || 'Fund';
    }
    get investmentTypeColumnHeading(): string {
        return this.block?.data?.investmentTypeColumnHeading || 'Investment Type';
    }
    get accountGroupHeading(): string {
        return this.block?.data?.accountGroupHeading || '';
    }
    get accountGroupHeadingDC(): string {
        return this.block?.data?.accountGroupHeadingDC || 'Account (Extra or DC)';
    }
    get accountGroupHeadingIA(): string {
        return this.block?.data?.accountGroupHeadingIA || 'Account (Investment)';
    }
    get unitsColumnHeading(): string {
        return this.block?.data?.unitsColumnHeading || 'Units Held';
    }
    get valueColumnHeading(): string {
        return this.block?.data?.valueColumnHeading || 'Value (\u00A3)';
    }
    get totalLabel(): string {
        return this.block?.data?.totalLabel || 'Your account value';
    }
    get currencySymbol(): string {
        return this.block?.data?.currencySymbol || '\u00A3';
    }
    get glidepathFallbackLabel(): string {
        return this.block?.data?.glidepathFallbackLabel || 'Annuity Glidepath';
    }
    get projectedPensionHeading(): string {
        return this.block?.data?.projectedPensionHeading || 'Your Projected Pension';
    }
    get projectedColumnHeading(): string {
        return this.block?.data?.projectedColumnHeading || 'Projected to age';
    }
    get scenarioALabel(): string {
        return this.block?.data?.scenarioALabel || 'Scenario A';
    }
    get scenarioBLabel(): string {
        return this.block?.data?.scenarioBLabel || 'Scenario B';
    }
    get projectedFundLabel(): string {
        return this.block?.data?.projectedFundLabel || 'Projected fund value at your TPA of';
    }
    get estimatedPensionLabel(): string {
        return this.block?.data?.estimatedPensionLabel || 'Estimated pension at your TPA of';
    }
    get disclaimerText(): string | null {
        return this.block?.data?.disclaimerText || null;
    }
    get noDataMessage(): string {
        return this.block?.data?.noDataMessage || 'We cannot currently present your SMPI';
    }
    get introText(): string | null {
        return this.block?.data?.introText || null;
    }
    get footnoteText(): string | null {
        return this.block?.data?.footnoteText || null;
    }
    get assumptionsText(): string | null {
        return this.block?.data?.assumptionsText || null;
    }

    getAccountGroupHeadingForType(tableType: number): string {
        if (this.accountGroupHeading) return this.accountGroupHeading;
        return tableType === 3 ? 'Investment Account' : 'Account';
    }

    getIntroTextWithTRA(): string | null {
        const text = this.introText;
        if (!text) return null;
        const tra = this.activeSMPI()?.SMPI_TRA || '';
        return text.replace(/\{\{TRA}}/g, tra);
    }

    activeSMPI = computed(() => {
        const year = this.activeYear();
        const data = this.smpiByYear();
        return year && data[year] ? data[year] : null;
    });

    fundRange = computed(() => {
        const smpi = this.activeSMPI();
        if (!smpi) return [];
        const funds: number[] = [];
        for (let n = 1; n <= 16; n++) {
            if (smpi['FUND_' + n]) {
                funds.push(n);
            }
        }
        return funds;
    });

    constructor() {
        const parser = inject(ParserService);
        const auth = inject(AuthService);

        super(parser, auth);

        this.parser = parser;
        this.auth = auth;
    }

    override ngOnInit(): void {
        super.ngOnInit();
        const isMocked = !!this.block?.data?.isMocked;
        const collectionTypeId = this.block?.data?.chosenCollection;
        if (isMocked || collectionTypeId) {
            this.loadSmpiData(collectionTypeId, isMocked);
        }
    }

    setActiveYear(year: string): void {
        this.activeYear.set(year);
    }

    formatMoney(value: string | number | undefined, decimals = 2): string {
        if (value === undefined || value === null || value === '') return '0.00';
        const num = typeof value === 'string' ? parseFloat(value) : value;
        if (isNaN(num)) return '0.00';
        return num.toLocaleString('en-GB', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        });
    }

    isGlidepath(fundIndex: number): boolean {
        const smpi = this.activeSMPI();
        return smpi ? smpi.GLIDEPATH === fundIndex : false;
    }

    isGreyCell(fundIndex: number): boolean {
        return fundIndex >= 13;
    }

    private loadSmpiData(collectionTypeId: number, isMocked = false): void {
        let mockCollections: any[] | null = null;
        if (isMocked) {
            try {
                mockCollections = JSON.parse(this.block?.data?.mockDataJson || '');
            } catch {
                mockCollections = null;
            }
        }

        const data$ = mockCollections
            ? new Observable(subscriber => {
                  subscriber.next(mockCollections);
                  subscriber.complete();
              })
            : this.getUserCollections(collectionTypeId, null, true);

        data$
            .pipe(
                map((collections: any) => (Array.isArray(collections) ? collections : [])),
                map((collections: any[]) => this.processSmpiCollections(collections)),
            )
            .subscribe({
                next: ({ years, byYear }) => {
                    this.smpiYears.set(years);
                    this.smpiByYear.set(byYear);
                    if (years.length > 0) {
                        this.activeYear.set(years[0]);
                    }
                    this.loading.set(false);
                },
                error: err => {
                    console.error('Error loading SMPI data:', err);
                    this.loading.set(false);
                },
            });
    }

    private processSmpiCollections(collections: any[]): { years: string[]; byYear: Record<string, SmpiContent> } {
        const byYear: Record<string, SmpiContent> = {};

        collections.forEach((smpi: any) => {
            const year = smpi.identifiers?.YEAR;
            if (!year || !smpi.content || !smpi.content[0]) return;

            const content = smpi.content[0] as SmpiContent;
            content.SMPI_TABLE = parseInt(String(content.SMPI_TABLE || 0), 10);

            // Determine glidepath fund index
            if (content['UNITS_13'] || content['UNITS_14'] || content['UNITS_15'] || content['UNITS_16']) {
                content.GLIDEPATH = content['UNITS_13'] ? 13 : content['UNITS_14'] ? 14 : content['UNITS_15'] ? 15 : 16;
            }

            if (content['UNITS_13_IA'] || content['UNITS_14_IA'] || content['UNITS_15_IA'] || content['UNITS_16_IA']) {
                content.GLIDEPATH = content['UNITS_13_IA']
                    ? 13
                    : content['UNITS_14_IA']
                      ? 14
                      : content['UNITS_15_IA']
                        ? 15
                        : 16;
            }

            byYear[year] = content;
        });

        const years = Object.keys(byYear).sort((a, b) => Number(b) - Number(a));

        return { years, byYear };
    }

    private getUserCollections(typeId: number, identifiers?: unknown | null, includeContent?: boolean) {
        let params = new HttpParams().set('content', includeContent ? 'true' : 'false');
        if (identifiers !== null && identifiers !== undefined) {
            params = params.set('identifiers', JSON.stringify(identifiers || {}));
        }
        params = params.set('interact', 'false').set('typeId', typeId);
        return this.http.get('user-collections', { params });
    }
}
